export const ORDER_STATUS_MAP: Record<string, { label: string; color: string; bgColor: string }> = {
  // Logistics statuses
  "Preparing to ship": {
    label: "Đang chuẩn bị hàng",
    color: "text-orange-600",
    bgColor: "bg-orange-100"
  },
  "Package picked up": {
    label: "Đã lấy hàng",
    color: "text-orange-600",
    bgColor: "bg-orange-100"
  },
  "In transit": {
    label: "Đang vận chuyển",
    color: "text-orange-600",
    bgColor: "bg-orange-100"
  },
  "Out for delivery": {
    label: "Đang giao hàng",
    color: "text-orange-600",
    bgColor: "bg-orange-100"
  },
  "International transit": {
    label: "Đang vận chuyển quốc tế",
    color: "text-orange-600",
    bgColor: "bg-orange-100"
  },
  Delivered: { label: "Đã giao hàng", color: "text-green-600", bgColor: "bg-green-100" }
}

// Translation map for logistics event titles
const LOGISTICS_TITLE_MAP: Record<string, string> = {
  "Preparing to ship": "Đang chuẩn bị hàng",
  "Package picked up": "Đã lấy hàng",
  "In transit": "Đang vận chuyển",
  "International transit": "Đang vận chuyển quốc tế",
  "Out for delivery": "Đang giao hàng",
  Delivered: "Đã giao hàng"
}

// ============================================================================
// Dictionary-based fallback for word/phrase-level translation
// Sorted by phrase length (longest first) to avoid partial replacements
// ============================================================================
const LOGISTICS_DICTIONARY: [RegExp, string][] = [
  // Long phrases first
  [/country\/region of origin/gi, "nước/khu vực gửi hàng"],
  [/country\/region of destination/gi, "nước/khu vực nhận hàng"],
  [/sorting center/gi, "trung tâm phân loại"],
  [/delivery hub/gi, "điểm giao hàng"],
  [/delivery station/gi, "trạm giao hàng"],
  [/customs clearance/gi, "thông quan"],
  [/transit warehouse/gi, "kho trung chuyển"],
  [/distribution center/gi, "trung tâm phân phối"],
  [/next carrier/gi, "đơn vị vận chuyển tiếp theo"],
  [/carrier pickup/gi, "đơn vị vận chuyển đến lấy"],
  [/out for delivery/gi, "đang giao hàng"],
  [/in transit/gi, "đang vận chuyển"],
  [/on the way/gi, "đang trên đường"],
  [/picked up/gi, "đã lấy hàng"],
  [/handed over/gi, "đã bàn giao"],
  [/attempt failed/gi, "không thành công"],
  // Single words
  [/\bdelivered\b/gi, "đã giao"],
  [/\bdelivery\b/gi, "giao hàng"],
  [/\bdeparted\b/gi, "đã rời"],
  [/\barrived\b/gi, "đã đến"],
  [/\bpackage\b/gi, "kiện hàng"],
  [/\bparcel\b/gi, "bưu kiện"],
  [/\bshipment\b/gi, "lô hàng"],
  [/\border\b/gi, "đơn hàng"],
  [/\bcarrier\b/gi, "đơn vị vận chuyển"],
  [/\bcustoms\b/gi, "hải quan"],
  [/\bseller\b/gi, "người bán"],
  [/\bbuyer\b/gi, "người mua"],
  [/\brecipient\b/gi, "người nhận"],
  [/\bcustomer\b/gi, "khách hàng"],
  [/\bwarehouse\b/gi, "kho hàng"],
  [/\bhub\b/gi, "trung tâm"],
  [/\bpreparing\b/gi, "đang chuẩn bị"],
  [/\bprocessing\b/gi, "đang xử lý"],
  [/\bpacked\b/gi, "đã đóng gói"],
  [/\bshipped\b/gi, "đã gửi"],
  [/\breturned\b/gi, "đã trả lại"],
  [/\breturn\b/gi, "trả lại"],
  [/\bcancelled\b/gi, "đã hủy"],
  [/\bcompleted\b/gi, "hoàn tất"],
  [/\bfailed\b/gi, "thất bại"],
  [/\bpending\b/gi, "đang chờ"],
  [/\bconfirmed\b/gi, "đã xác nhận"],
  [/\btransit\b/gi, "vận chuyển"],
  [/\binternational\b/gi, "quốc tế"],
  [/\bdomestic\b/gi, "nội địa"],
  [/\borigin\b/gi, "xuất phát"],
  [/\bdestination\b/gi, "đích đến"],
  [/\bstation\b/gi, "trạm"],
  [/\bcenter\b/gi, "trung tâm"],
  [/\bawaiting\b/gi, "đang chờ"],
  [/\btransport\b/gi, "vận chuyển"],
  [/\bpickup\b/gi, "lấy hàng"],
  [/\bcollected\b/gi, "đã thu gom"]
]

// Apply dictionary-based translation for remaining English words
const translateByDictionary = (text: string): string => {
  let result = text
  for (const [pattern, replacement] of LOGISTICS_DICTIONARY) {
    result = result.replace(pattern, replacement)
  }
  return result
}

// ============================================================================
// Auto-translate via MyMemory API (free, no API key needed)
// Cache results in memory to avoid duplicate API calls
// ============================================================================
const translationCache = new Map<string, string>()

const hasEnglishText = (text: string): boolean => {
  // Check if the text still contains English words (3+ consecutive letters)
  const withoutLocations = text.replace(/\b[A-Z][a-z]*\b/g, "") // ignore capitalized proper nouns
  return /[a-zA-Z]{3,}/.test(withoutLocations)
}

const autoTranslateAsync = async (text: string): Promise<string> => {
  // Check cache first
  const cached = translationCache.get(text)
  if (cached) return cached

  try {
    const url = `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=en|vi`
    const response = await fetch(url)
    if (!response.ok) return text

    const data = await response.json()
    const translated = data?.responseData?.translatedText
    if (translated && translated !== text) {
      translationCache.set(text, translated)
      return translated
    }
  } catch {
    // Network error — silently return original text
  }
  return text
}

// ============================================================================
// Main translation functions
// ============================================================================

// Helper function to translate logistics title
export const translateTitle = (title: string): string => {
  // Check exact match first
  if (LOGISTICS_TITLE_MAP[title]) {
    return LOGISTICS_TITLE_MAP[title]
  }
  // Check partial match for common patterns
  for (const [key, value] of Object.entries(LOGISTICS_TITLE_MAP)) {
    if (title.toLowerCase().includes(key.toLowerCase())) {
      return value
    }
  }
  // Fallback: dictionary-based translation
  return translateByDictionary(title)
}

// Async version for components that support async rendering
export const translateTitleAsync = async (title: string): Promise<string> => {
  const result = translateTitle(title)
  if (hasEnglishText(result)) {
    return autoTranslateAsync(title)
  }
  return result
}

// Helper function to translate logistics description (sync with regex + dictionary)
export const translateDescription = (description: string): string => {
  const result = description
    // Seller preparing / packing
    .replace(
      /The seller is preparing your package.*shipping\./gi,
      "Người bán đang chuẩn bị hàng và sẽ giao cho đơn vị vận chuyển."
    )
    .replace(
      /Your order was packed by the seller and is awaiting carrier pickup and transport to their hub\./gi,
      "Đơn hàng đã được người bán đóng gói và đang chờ đơn vị vận chuyển đến lấy."
    )
    .replace(/Your order was packed by the seller/gi, "Đơn hàng đã được người bán đóng gói")
    .replace(
      /is awaiting carrier pickup and transport to their hub/gi,
      "đang chờ đơn vị vận chuyển đến lấy hàng"
    )
    // Carrier handover
    .replace(
      /Handed over to next carrier in country\/region of origin/gi,
      "Đã bàn giao cho đơn vị vận chuyển tiếp theo tại nước/khu vực gửi hàng"
    )
    .replace(/Handed over to next carrier in/gi, "Đã bàn giao cho đơn vị vận chuyển tiếp theo tại")
    // Transit in country/region
    .replace(
      /In transit in country\/region of origin\.?\s*/gi,
      "Đang vận chuyển tại nước/khu vực gửi hàng. "
    )
    .replace(
      /In transit in country\/region of destination\.?\s*/gi,
      "Đang vận chuyển tại nước/khu vực nhận hàng. "
    )
    // Sorting center
    .replace(/Departed sorting center in/gi, "Đã rời trung tâm phân loại tại")
    .replace(
      /Arrived at sorting center in country\/region of origin/gi,
      "Đã đến trung tâm phân loại tại nước/khu vực gửi hàng"
    )
    .replace(
      /Arrived at sorting center in country\/region of destination/gi,
      "Đã đến trung tâm phân loại tại nước/khu vực nhận hàng"
    )
    .replace(/Arrived at sorting center in/gi, "Đã đến trung tâm phân loại tại")
    .replace(
      /Package arrived at the sorting center in/gi,
      "Kiện hàng đã đến trung tâm phân loại tại"
    )
    .replace(/Package left the sorting center in/gi, "Kiện hàng đã rời trung tâm phân loại tại")
    // Delivery hub
    .replace(/Package arrived at the delivery hub in/gi, "Kiện hàng đã đến điểm giao hàng tại")
    // Customs
    .replace(/Customs clearance completed/gi, "Đã hoàn tất thông quan")
    .replace(/Customs clearance in progress/gi, "Đang thông quan")
    .replace(/Arrived at customs/gi, "Đã đến hải quan")
    // Package pickup & collection
    .replace(/Package collected by our carrier in/gi, "Kiện hàng đã được lấy tại")
    .replace(/Package picked up by carrier/gi, "Kiện hàng đã được đơn vị vận chuyển lấy")
    // Delivery
    .replace(/Your package is out for delivery\./gi, "Kiện hàng đang được giao đến bạn.")
    .replace(/Your package has been delivered\./gi, "Kiện hàng đã được giao thành công.")
    .replace(/Delivery attempt failed/gi, "Giao hàng không thành công")
    // General terms
    .replace(/Recipient:/gi, "Người nhận:")
    .replace(/Customer/gi, "Khách hàng")
    .replace(/country\/region of origin/gi, "nước/khu vực gửi hàng")
    .replace(/country\/region of destination/gi, "nước/khu vực nhận hàng")

  // If still has English after regex, apply dictionary fallback
  if (hasEnglishText(result)) {
    return translateByDictionary(result)
  }
  return result
}

// Async version: regex → dictionary → API translation
export const translateDescriptionAsync = async (description: string): Promise<string> => {
  const result = translateDescription(description)
  // If dictionary fallback still leaves English text, call API
  if (hasEnglishText(result)) {
    return autoTranslateAsync(description)
  }
  return result
}
